import { I18n } from "@assets/strings";
import React from "react";
import { useHistory } from "@core/routing/useHistory";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { useAsync } from "@utils/hooks";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { ErrorContainer } from "@ui/components/containers/errorContainer";
import { LockIcon } from "@ui/components/icons";
import { useForm } from "@ui/components/inputs/hooks";
import { SimpleTextInput } from "@ui/components/inputs/simpleTextInput";
import { gap } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";

interface PasswordResetFormProps extends React.HTMLAttributes<HTMLFormElement> {
	oobCode: string;
}
export const PasswordResetForm: React.FC<PasswordResetFormProps> = ({ oobCode, ...htmlProps }) => {
	const { checkSubmit, fieldProps, formValue } = useForm({ password: "", confirm: "" }, ["password", "confirm"], {
		password: (p) => p.length >= 6,
		confirm: (p, { password }) => p === password,
	});
	const { authService } = useServices();

	const { run, loading, error } = useAsync(
		() => authService.resetPassword(oobCode, formValue.password),
		[formValue.password]
	);
	const history = useHistory();

	return (
		<Container
			{...htmlProps}
			onSubmit={async (e) => {
				e.preventDefault();
				if (!checkSubmit()) {
					return;
				}
				await run();
				createToast(I18n.t("success"), I18n.t("password.reset.success"), "success");
				history.push("/");
			}}
		>
			<Title>{I18n.t("password.reset.title")}</Title>
			<Fields>
				<FieldWrapper>
					<label htmlFor="password-reset-password">{I18n.t("password.new.label")}</label>
					<SimpleTextInput
						id="password-reset-password"
						color="primary"
						icon={<LockIcon />}
						iconPosition="left"
						type="password"
						autoComplete="new-password"
						{...fieldProps("password")}
						placeholder={I18n.t("password.placeholder")}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<label htmlFor="password-reset-confirm">{I18n.t("password.confirm.label")}</label>
					<SimpleTextInput
						id="password-reset-confirm"
						color="primary"
						icon={<LockIcon />}
						iconPosition="left"
						type="password"
						autoComplete="new-password"
						{...fieldProps("confirm")}
						placeholder={I18n.t("password.placeholder")}
					/>
				</FieldWrapper>
				{error && <ErrorContainer>{I18n.t("auth.error.default")}</ErrorContainer>}
			</Fields>
			<LoadingButton loading={loading} type="submit">
				{I18n.t("validate")}
			</LoadingButton>
		</Container>
	);
};

const Container = styled.form`
	${gap(54)}
`;

const Title = styled.div`
	${({ theme }) => theme.textStyles.title};
`;

const Fields = styled.div`
	${gap(30)}
`;

const FieldWrapper = styled.div`
	label {
		display: block;
		${({ theme }) => theme.textStyles.primaryMedium};
		margin-bottom: 16px;
	}
`;
